import { isLocalDateTime, RecordType, type AnyEnvelope } from '@wiztime/core';
import type { Repo, UndoEntry } from '@wiztime/storage';
import { formatRange } from './format';

/**
 * What an undo step did to the records, in words the user knows: which object (a check-in's
 * title, a block's project and time, a project's name) and, where the step flips something, the
 * state it leaves (ticked, enabled, deleted ...). The step itself only holds record images;
 * this turns them into the text of the undo toast and of the action history.
 */

export const StepState = {
  Ticked: 'ticked',
  Unticked: 'unticked',
  Enabled: 'enabled',
  Disabled: 'disabled',
  Created: 'created',
  Deleted: 'deleted',
  Restored: 'restored',
} as const;
export type StepState = (typeof StepState)[keyof typeof StepState];

/** Which images of a step the description reads. */
export const StepView = {
  /** The state an undo leaves: the images before the step. */
  Undone: 'undone',
  /** What the step itself did: the images after it. */
  Applied: 'applied',
} as const;
export type StepView = (typeof StepView)[keyof typeof StepView];

export interface StepDetail {
  /** The object: "Drink water", "Work 09:00–10:30 (+2)", null when the step has no nameable object. */
  readonly subject: string | null;
  readonly state: StepState | null;
}

/** Names of projects and check-in items by record id; see `resolveNames`. */
export type NameIndex = ReadonlyMap<string, string>;

const present = (image: AnyEnvelope | null): image is AnyEnvelope & { deleted: false } =>
  image !== null && !image.deleted && image.data !== null;

const imageOf = (entry: UndoEntry, view: StepView): AnyEnvelope | null =>
  view === StepView.Undone ? entry.before : entry.after;
const otherImageOf = (entry: UndoEntry, view: StepView): AnyEnvelope | null =>
  view === StepView.Undone ? entry.after : entry.before;

/** The image to read the object's identity from: the one that exists, the visible one first. */
function identityOf(entry: UndoEntry, view: StepView): AnyEnvelope {
  const image = imageOf(entry, view);
  return present(image) ? image : otherImageOf(entry, view) ?? entry.after;
}

/** "Created" when the object did not exist before the step, "restored" when it was deleted. */
function appearance(entry: UndoEntry, view: StepView): StepState {
  const other = otherImageOf(entry, view);
  return other === null ? StepState.Created : StepState.Restored;
}

function presenceState(entry: UndoEntry, view: StepView): StepState | null {
  const image = imageOf(entry, view);
  if (!present(image)) return StepState.Deleted;
  return present(otherImageOf(entry, view)) ? null : appearance(entry, view);
}

/**
 * A completion change of the step. A legacy log moved to its occurrence id leaves a tombstone of
 * the old id in the same step (data dictionary §3.2); the tombstone is not a change the user made.
 */
const isCheckinChange = (entry: UndoEntry): boolean =>
  entry.after.type === RecordType.CheckinLog && !(entry.after.deleted && entry.before !== null && !entry.before.deleted);

/**
 * "10-13 03:00" of a timed occurrence: an item has two occurrences on the day its planned time
 * moved across the day start, so the step names which one. Null for an untimed or legacy log.
 */
function occurrenceText(occursAt: unknown): string | null {
  return isLocalDateTime(occursAt) ? `${occursAt.slice(5, 10)} ${occursAt.slice(11)}` : null;
}

export function describeStep(entries: readonly UndoEntry[], view: StepView, names: NameIndex): StepDetail {
  const blocks = entries.filter((entry) => entry.after.type === RecordType.TimeBlock);
  const first = blocks[0];
  if (first !== undefined) {
    const identity = identityOf(first, view);
    const data = identity.type === RecordType.TimeBlock ? identity.data : null;
    if (data === null) return { subject: null, state: null };
    const project = names.get(data.projectId) ?? null;
    const head = [project, formatRange(data.startMin, data.endMin)].filter((part) => part !== null).join(' ');
    return {
      subject: blocks.length > 1 ? `${head} (+${blocks.length - 1})` : head,
      state: presenceState(first, view),
    };
  }
  const logs = entries.filter(isCheckinChange);
  const firstLog = logs[0];
  if (firstLog !== undefined) {
    const identity = identityOf(firstLog, view);
    const image = imageOf(firstLog, view);
    if (identity.type !== RecordType.CheckinLog || identity.data === null) return { subject: null, state: null };
    const head = [names.get(identity.data.itemId) ?? null, occurrenceText(identity.data.occursAt)]
      .filter((part) => part !== null)
      .join(' ');
    const done = present(image) && image.type === RecordType.CheckinLog && image.data?.done === true;
    return {
      subject: head === '' ? null : logs.length > 1 ? `${head} (+${logs.length - 1})` : head,
      state: done ? StepState.Ticked : StepState.Unticked,
    };
  }
  const entry = entries.find((candidate) => DETAILED_TYPES.has(candidate.after.type));
  if (entry === undefined) return { subject: null, state: null };
  const identity = identityOf(entry, view);
  const image = imageOf(entry, view);
  switch (identity.type) {
    case RecordType.CheckinItem:
    case RecordType.Project: {
      const subject = names.get(identity.id) ?? identity.data?.name ?? null;
      const presence = presenceState(entry, view);
      if (presence !== null) return { subject, state: presence };
      const other = otherImageOf(entry, view);
      if (
        present(image) &&
        present(other) &&
        image.type === identity.type &&
        other.type === identity.type &&
        image.data?.active !== other.data?.active
      ) {
        return { subject, state: image.data?.active === true ? StepState.Enabled : StepState.Disabled };
      }
      return { subject, state: null };
    }
    case RecordType.DailyNote:
      return { subject: identity.data?.personalDate ?? null, state: null };
    default:
      return { subject: null, state: null };
  }
}

const DETAILED_TYPES: ReadonlySet<string> = new Set([
  RecordType.CheckinItem,
  RecordType.Project,
  RecordType.DailyNote,
]);

/**
 * The names that `describeStep` needs for these steps: the project of every block and the item
 * of every check-in log, read from the database, plus the names the steps carry themselves (the
 * images of a deleted project or item still hold its name). A name that cannot be found is
 * missing from the index and the description leaves it out.
 */
export async function resolveNames(repo: Repo, steps: ReadonlyArray<readonly UndoEntry[]>): Promise<NameIndex> {
  const names = new Map<string, string>();
  const wanted = new Set<string>();
  for (const entries of steps) {
    for (const entry of entries) {
      for (const image of [entry.before, entry.after]) {
        if (image === null || image.data === null) continue;
        if (image.type === RecordType.Project || image.type === RecordType.CheckinItem) {
          names.set(image.id, image.data.name);
        } else if (image.type === RecordType.TimeBlock) {
          wanted.add(image.data.projectId);
        } else if (image.type === RecordType.CheckinLog) {
          wanted.add(image.data.itemId);
        }
      }
    }
  }
  const missing = [...wanted].filter((id) => !names.has(id));
  const stored = await repo.getMany(missing);
  for (const record of stored) {
    if (record === undefined || record.data === null) continue;
    if (record.type === RecordType.Project || record.type === RecordType.CheckinItem) {
      names.set(record.id, record.data.name);
    }
  }
  return names;
}
